import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex bg-zinc-950 text-zinc-100 p-12 flex-col justify-between">
        <div>
          <div className="font-heading text-2xl tracking-tight">PARAM SKILLS</div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mt-1">Training (I) Pvt. Ltd.</div>
        </div>
        <div>
          <h1 className="font-heading text-5xl leading-[0.95] tracking-tight">Reset your<br />sign-in<br />credentials.</h1>
          <p className="mt-6 text-zinc-300 max-w-md text-sm leading-relaxed">
            Enter your email and we'll send a one-time reset link. The link expires in 60 minutes.
          </p>
        </div>
        <div className="text-xs text-zinc-500">© 2026 Param Skills.</div>
      </div>

      <div className="bg-white flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md" data-testid="forgot-password-root">
          <Link to="/login" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-1">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mt-6">Recovery</div>
          <h2 className="font-heading text-3xl mt-2 tracking-tight">Forgot your password?</h2>
          <p className="text-sm text-zinc-500 mt-1">We'll email you a secure link to set a new one.</p>

          {sent ? (
            <div className="mt-8 border-l-2 border-emerald-600 bg-emerald-50 pl-4 py-4" data-testid="forgot-sent">
              <div className="font-medium text-emerald-900">Check your inbox</div>
              <div className="text-sm text-emerald-800 mt-1">
                If <span className="font-mono">{email}</span> matches an account, a reset link is on its way. It expires in 60 minutes.
              </div>
              <div className="mt-4 flex gap-3">
                <Link to="/login"><Button variant="outline" className="rounded-sm h-9 border-zinc-300">Return to sign in</Button></Link>
                <button onClick={() => { setSent(false); setEmail(""); }} className="text-xs uppercase tracking-widest text-zinc-600 hover:text-black">Try a different email</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="fp-email" className="text-xs uppercase tracking-widest">Email address</Label>
                <Input id="fp-email" type="email" required autoFocus autoComplete="email"
                       value={email} onChange={(e) => setEmail(e.target.value)}
                       data-testid="forgot-email"
                       className="mt-2 rounded-sm border-zinc-300 h-11" />
              </div>
              <Button type="submit" disabled={busy || !email} data-testid="forgot-submit"
                      className="w-full h-11 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white font-medium tracking-wide">
                {busy ? "Sending…" : "Send reset link →"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
